import * as React from "react";
import { Chip, ChipGroup, FormField, Input } from "@chotca/ui";
import type { SalaryType } from "@chotca/config";
import {
  SALARY_PRESETS,
  type SalarySanityWarning,
  checkSalarySanity,
  formatVnd,
  parseVnd,
} from "./helpers";

// ── Stepper (− N +) ────────────────────────────────────────────────────────
export interface StepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  ariaLabel?: string;
}

export function Stepper({
  value,
  onChange,
  min = 1,
  max = 50,
  ariaLabel,
}: StepperProps) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  const safe = Number.isFinite(value) ? value : min;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex h-12 items-center rounded-[var(--radius-sm)] border-[1.5px] border-smoke bg-mist"
    >
      <button
        type="button"
        onClick={dec}
        disabled={safe <= min}
        className="grid h-full w-12 place-items-center text-lg font-bold text-ink hover:bg-paper disabled:opacity-40"
        aria-label="Giảm"
      >
        −
      </button>
      <div className="grid h-full w-14 place-items-center text-[15px] font-bold tabular-nums">
        {safe}
      </div>
      <button
        type="button"
        onClick={inc}
        disabled={safe >= max}
        className="grid h-full w-12 place-items-center text-lg font-bold text-ink hover:bg-paper disabled:opacity-40"
        aria-label="Tăng"
      >
        +
      </button>
    </div>
  );
}

// ── SalaryRangeInput ───────────────────────────────────────────────────────
const SALARY_UNIT_OPTIONS: ReadonlyArray<{ value: SalaryType; label: string }> =
  [
    { value: "PER_MONTH", label: "/tháng" },
    { value: "PER_DAY", label: "/ngày" },
    { value: "PER_SHIFT", label: "/ca" },
    { value: "PER_HOUR", label: "/giờ" },
  ];

export interface SalaryRangeInputProps {
  min: number;
  max: number;
  type: SalaryType;
  onMinChange: (n: number) => void;
  onMaxChange: (n: number) => void;
  onTypeChange: (t: SalaryType) => void;
  errorMin?: string;
  errorMax?: string;
}

export function SalaryRangeInput({
  min,
  max,
  type,
  onMinChange,
  onMaxChange,
  onTypeChange,
  errorMin,
  errorMax,
}: SalaryRangeInputProps) {
  const presets = SALARY_PRESETS[type] ?? [];
  // "Thoả thuận" is simply both amounts left at 0 — no separate flag.
  const negotiable = min === 0 && max === 0;

  // Soft "có phải bạn chọn nhầm đơn vị?" check — e.g. 10tr/giờ or 10k/tháng.
  // Never blocks submit; only nudges. Debounced so it surfaces after the user
  // pauses, not on every keystroke (otherwise "6→60→600…" flashes "too low"
  // while a monthly figure is still being typed). Skipped while negotiable.
  const [sanity, setSanity] = React.useState<SalarySanityWarning | null>(null);
  React.useEffect(() => {
    if (negotiable) {
      setSanity(null);
      return;
    }
    const id = setTimeout(
      () => setSanity(checkSalarySanity(min, max, type)),
      500,
    );
    return () => clearTimeout(id);
  }, [min, max, type, negotiable]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-ink">Mức lương</span>
        {negotiable ? (
          <span className="inline-flex items-center rounded-full bg-ember/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-ember">
            Thoả thuận
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              onMinChange(0);
              onMaxChange(0);
            }}
            className="text-xs font-semibold text-ink/55 underline-offset-4 hover:text-ink hover:underline"
          >
            Để lương thoả thuận
          </button>
        )}
      </div>

      <ChipGroup<SalaryType>
        mode="single"
        tone="lime"
        size="sm"
        value={type}
        onChange={(v) => onTypeChange(v)}
        options={
          SALARY_UNIT_OPTIONS as Array<{ value: SalaryType; label: string }>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Lương tối thiểu" error={errorMin}>
          <div className="relative">
            <Input
              inputMode="numeric"
              value={formatVnd(min)}
              onChange={(e) => onMinChange(parseVnd(e.target.value))}
              placeholder="VD: 6.000.000"
              invalid={!!errorMin}
              className="pr-14"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-ash">
              VNĐ
            </span>
          </div>
        </FormField>
        <FormField label="Lương tối đa" error={errorMax}>
          <div className="relative">
            <Input
              inputMode="numeric"
              value={formatVnd(max)}
              onChange={(e) => onMaxChange(parseVnd(e.target.value))}
              placeholder="VD: 8.000.000"
              invalid={!!errorMax}
              className="pr-14"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-ash">
              VNĐ
            </span>
          </div>
        </FormField>
      </div>

      {sanity ? (
        <div className="flex flex-col gap-2 rounded-xl border border-sun-dark/30 bg-sun-light px-4 py-3 text-xs leading-relaxed text-ink/80">
          <p className="font-bold text-warning">Kiểm tra lại mức lương</p>
          <p>{sanity.message}</p>
          {sanity.suggestedType ? (
            <button
              type="button"
              onClick={() => {
                onTypeChange(sanity.suggestedType!);
                setSanity(null);
              }}
              className="self-start rounded-full border border-warning/40 bg-paper px-3 py-1 font-semibold text-warning transition-colors hover:bg-warning/10"
            >
              Chuyển sang đơn vị {sanity.suggestedLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      {presets.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink/60">Gợi ý nhanh:</span>
          {presets.map((p) => {
            const active = p.min === min && p.max === max;
            return (
              <Chip
                key={p.label}
                size="sm"
                tone="lime"
                selected={active}
                onClick={() => {
                  onMinChange(p.min);
                  onMaxChange(p.max);
                }}
              >
                {p.label}
              </Chip>
            );
          })}
        </div>
      ) : null}

      {negotiable ? (
        <div className="rounded-xl border border-ember/30 bg-ember/10 px-4 py-3 text-xs leading-relaxed text-ink/80">
          <p className="font-bold text-ember">Đang để lương "Thoả thuận"</p>
          <p className="mt-1">
            Tin không ghi lương ít được ưu tiên hiển thị trên feed và thường bị
            ứng viên lướt qua. Nhập một khoảng lương cụ thể (dù chỉ là ước
            lượng) để tin tiếp cận nhiều người và nhận hồ sơ nhanh hơn.
          </p>
        </div>
      ) : (
        <p className="text-xs text-ink/55">
          Tin ghi rõ khoảng lương được ưu tiên hiển thị và thu hút nhiều ứng
          viên hơn so với để "Thoả thuận".
        </p>
      )}
    </div>
  );
}
